import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Share2, Clock, Users, CheckCircle2, Flame, Zap, Maximize2 } from 'lucide-react';
import { ShareToNostrDialog } from '@/components/ShareToNostrDialog';
import { FullScreenPoll } from '@/components/FullScreenPoll';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePollResults } from '@/hooks/usePollResults';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import type { Poll } from '@/hooks/usePolls';
import { genUserName } from '@/lib/genUserName';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/useToast';

interface VoterAvatarsProps {
  voterPubkeys: string[];
}

function VoterAvatars({ voterPubkeys }: VoterAvatarsProps) {
  const maxDisplay = 8;
  const displayPubkeys = voterPubkeys.slice(0, maxDisplay);
  const remaining = voterPubkeys.length - maxDisplay;

  return (
    <div className="flex items-center gap-1">
      <TooltipProvider>
        {displayPubkeys.map((pubkey, index) => (
          <div 
            key={pubkey} 
            className="animate-scale-in-bounce"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <VoterAvatar pubkey={pubkey} />
          </div>
        ))}
        {remaining > 0 && (
          <div className="h-7 w-7 rounded-full glass dark:glass-dark border border-orange-500/30 flex items-center justify-center text-xs font-bold text-orange-500 animate-scale-in">
            +{remaining}
          </div>
        )}
      </TooltipProvider>
    </div>
  );
}

interface VoterAvatarProps {
  pubkey: string;
}

function VoterAvatar({ pubkey }: VoterAvatarProps) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || metadata?.display_name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;

  return (
    <Tooltip>
      <TooltipTrigger>
        <Avatar className="h-7 w-7 ring-1 ring-orange-500/30 border border-orange-500/20 hover:ring-orange-500 hover:scale-110 transition-all duration-300 cursor-pointer">
          <AvatarImage src={avatarUrl} alt={displayName} />
          <AvatarFallback className="text-xs bg-gradient-to-br from-orange-500/20 to-amber-500/20 font-bold text-orange-600 dark:text-orange-400">
            {displayName[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </TooltipTrigger>
      <TooltipContent className="glass dark:glass-dark border border-orange-500/30">
        <p className="font-bold">{displayName}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Animated counter component
function AnimatedNumber({ value, className }: { value: number; className?: string }) {
  const [displayValue, setDisplayValue] = useState(value);
  const prevValue = useRef(value);

  useEffect(() => {
    if (value !== prevValue.current) {
      // Animate from previous to new value
      const startValue = prevValue.current;
      const endValue = value;
      const duration = 500;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease out cubic
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(startValue + (endValue - startValue) * easeOut);
        
        setDisplayValue(current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
      prevValue.current = value;
    }
  }, [value]);

  return (
    <span className={className}>
      {displayValue}
    </span>
  );
}

// Animated percentage component
function AnimatedPercentage({ value }: { value: number }) {
  const [displayValue, setDisplayValue] = useState(value);
  const prevValue = useRef(value);

  useEffect(() => {
    if (value !== prevValue.current) {
      const startValue = prevValue.current;
      const endValue = value;
      const duration = 800;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const current = startValue + (endValue - startValue) * easeOut;
        
        setDisplayValue(current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
      prevValue.current = value;
    }
  }, [value]);

  return (
    <span className="font-black text-xl tabular-nums bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent min-w-[4rem] text-right inline-block">
      {displayValue.toFixed(1)}%
    </span>
  );
}

interface PollCardProps {
  poll: Poll;
  detailed?: boolean;
  onExpand?: () => void;
}

export function PollCard({ poll, detailed = false, onExpand }: PollCardProps) {
  const author = useAuthor(poll.pubkey);
  const { user } = useCurrentUser();
  const results = usePollResults(poll, user?.pubkey);
  const { mutate: publishVote, isPending: isVoting } = useNostrPublish();
  const { toast } = useToast();

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [showVoteRipple, setShowVoteRipple] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const metadata = author.data?.metadata;
  const displayName = metadata?.name || metadata?.display_name || genUserName(poll.pubkey);
  const avatarUrl = metadata?.picture;

  const isPollEnded = poll.endsAt ? Date.now() / 1000 > poll.endsAt : false;
  const userHasVoted = !!results.data?.userVote || hasVoted;
  const shouldShowResults = userHasVoted || isPollEnded || detailed;

  // Auto-submit vote when option is selected (for single choice)
  useEffect(() => {
    if (poll.pollType === 'singlechoice' && selectedOptions.length === 1 && !userHasVoted && !isVoting && user) {
      const timeoutId = setTimeout(() => {
        handleVote();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedOptions, poll.pollType, userHasVoted, isVoting, user]);

  const handleOptionToggle = (optionId: string) => {
    if (userHasVoted || isPollEnded || isVoting) return;

    setShowVoteRipple(optionId);
    setTimeout(() => setShowVoteRipple(null), 600);

    if (poll.pollType === 'singlechoice') {
      setSelectedOptions([optionId]);
    } else {
      setSelectedOptions(prev =>
        prev.includes(optionId)
          ? prev.filter(id => id !== optionId)
          : [...prev, optionId]
      );
    }
  };

  const handleVote = () => {
    if (!user) {
      toast({
        title: 'Login required',
        description: 'Please login to vote on polls',
        variant: 'destructive',
      });
      return;
    }

    if (selectedOptions.length === 0) {
      toast({
        title: 'Select an option',
        description: 'Please select at least one option to vote',
        variant: 'destructive',
      });
      return;
    }

    const tags: string[][] = [['e', poll.id]];
    selectedOptions.forEach(optionId => {
      tags.push(['response', optionId]);
    });

    publishVote(
      {
        kind: 1018,
        content: '',
        tags,
      },
      {
        onSuccess: () => {
          setHasVoted(true);
          toast({
            title: '🔥 Vote submitted!',
            description: 'Your vote has been recorded on the Nostr network',
          });
        },
        onError: () => {
          toast({
            title: 'Vote failed',
            description: 'Failed to submit your vote. Please try again.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const getPollUrl = () => {
    return `${window.location.origin}?poll=${poll.id}`;
  };

  const handleShare = async () => {
    const url = getPollUrl();

    try {
      if (navigator.share) {
        await navigator.share({
          title: poll.question,
          text: `Cast your vote! ${poll.question}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast({
          title: 'Link copied!',
          description: 'Poll link has been copied to clipboard',
        });
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Share failed:', err);
        try {
          await navigator.clipboard.writeText(url);
          toast({
            title: 'Link copied!',
            description: 'Poll link has been copied to clipboard',
          });
        } catch (clipboardErr) {
          console.error('Clipboard failed:', clipboardErr);
          toast({
            title: 'Share failed',
            description: 'Unable to share or copy link',
            variant: 'destructive',
          });
        }
      }
    }
  };

  const totalVotes = results.data?.totalVotes || 0;

  const handleExpand = () => {
    if (onExpand) {
      onExpand();
    } else {
      setIsFullScreen(true);
    }
  };

  const handleCloseFullScreen = () => {
    setIsFullScreen(false);
  };

  return (
    <>
      {/* Full Screen Modal */}
      {isFullScreen && (
        <FullScreenPoll poll={poll} onClose={handleCloseFullScreen} />
      )}
      
      <Card className="glass dark:glass-dark overflow-hidden border border-orange-500/20 dark:border-orange-500/30 shadow-2xl hover:shadow-[0_20px_60px_rgba(255,140,0,0.2)] transition-all duration-500 hover:scale-[1.01] group relative rounded-2xl">
      {/* Animated gradient border on hover */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute inset-[-1px] bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 rounded-2xl blur-sm opacity-30" />
      </div>
      
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 via-amber-500/0 to-orange-600/0 group-hover:from-orange-500/5 group-hover:via-amber-500/3 group-hover:to-orange-600/5 transition-all duration-500 pointer-events-none rounded-2xl" />
      
      <CardHeader className="space-y-4 relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative group/avatar">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full blur-md opacity-40 group-hover/avatar:opacity-70 transition-opacity duration-300 animate-breathe" />
              <Avatar className="h-12 w-12 ring-2 ring-orange-500/40 relative border-2 border-orange-500/20 transition-all duration-300 group-hover/avatar:scale-110">
                <AvatarImage src={avatarUrl} alt={displayName} />
                <AvatarFallback className="bg-gradient-to-br from-orange-500 to-amber-600 text-white font-bold">
                  {displayName[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="min-w-0">
              <p className="font-bold text-sm truncate">{displayName}</p>
              <p className="text-xs text-foreground/50 font-medium">
                {formatDistanceToNow(poll.created_at * 1000, { addSuffix: true })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Expand Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleExpand}
              className="h-9 w-9 rounded-xl glass dark:glass-dark border border-orange-500/20 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all duration-300 group/expand"
            >
              <Maximize2 className="h-4 w-4 text-orange-500/70 group-hover/expand:text-orange-500 group-hover/expand:scale-110 transition-all" />
            </Button>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {poll.pollType === 'multiplechoice' && (
            <Badge variant="secondary" className="shrink-0 glass dark:glass-dark border border-orange-500/30 font-bold text-orange-600 dark:text-orange-400 bg-orange-500/10">
              Multi-choice
            </Badge>
          )}
          {isPollEnded && (
            <Badge variant="outline" className="shrink-0 glass dark:glass-dark border border-orange-500/30 font-bold text-foreground/70">
              Ended
            </Badge>
          )}
        </div>

        <div className="cursor-pointer" onClick={handleExpand}>
          <h3 className="text-2xl font-black leading-tight tracking-tight">
            {poll.question}
          </h3>
        </div>

        {poll.endsAt && !isPollEnded && (
          <div className="flex items-center gap-2 text-sm text-foreground/60 font-bold glass dark:glass-dark px-4 py-2.5 rounded-xl border border-orange-500/20 w-fit">
            <Clock className="h-4 w-4 text-orange-500 animate-pulse" />
            <span>Ends {formatDistanceToNow(poll.endsAt * 1000, { addSuffix: true })}</span>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4 relative z-10">
        {poll.options.map((option, index) => {
          const voteCount = results.data?.optionCounts[option.id] || 0;
          const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
          const isSelected = selectedOptions.includes(option.id);
          const isUserVote = results.data?.userVote?.responseIds.includes(option.id);
          const isRippling = showVoteRipple === option.id;

          // Get voters for this option
          const votersForOption = results.data?.votes.filter(vote =>
            vote.responseIds.includes(option.id)
          ) || [];

          return (
            <div 
              key={option.id} 
              className="relative space-y-3"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {shouldShowResults ? (
                <div className="animate-fade-in">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-bold text-base truncate">{option.label}</span>
                      {isUserVote && (
                        <div className="flex items-center gap-1 bg-gradient-to-r from-orange-500/20 to-amber-500/20 px-2.5 py-1 rounded-full border border-orange-500/40 shrink-0 animate-scale-in-bounce">
                          <CheckCircle2 className="h-3.5 w-3.5 text-orange-500" />
                          <span className="text-xs font-black text-orange-500">You</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm text-foreground/50 font-bold tabular-nums">
                        <AnimatedNumber value={voteCount} />
                      </span>
                      <AnimatedPercentage value={percentage} />
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="relative overflow-hidden rounded-full h-4 bg-foreground/5 dark:bg-white/5 border border-orange-500/10">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 transition-all duration-1000 ease-out relative progress-shine rounded-full"
                      style={{ width: `${percentage}%` }}
                    >
                      {/* Animated shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                    </div>
                    {/* Glow effect at the end of progress */}
                    {percentage > 0 && (
                      <div 
                        className="absolute top-0 bottom-0 w-4 bg-gradient-to-r from-orange-500/50 to-transparent blur-sm"
                        style={{ left: `calc(${Math.min(percentage, 100)}% - 8px)` }}
                      />
                    )}
                  </div>

                  {/* Show voters if there are any */}
                  {votersForOption.length > 0 && (
                    <div className="pt-2">
                      <VoterAvatars voterPubkeys={votersForOption.map(v => v.pubkey)} />
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleOptionToggle(option.id)}
                  disabled={isPollEnded || userHasVoted || isVoting || !user}
                  className={`
                    w-full flex items-center justify-between gap-3 p-5 rounded-xl border-2 transition-all duration-300 font-bold text-base
                    glass dark:glass-dark relative overflow-hidden group/option
                    ${isSelected
                      ? 'border-orange-500 bg-gradient-to-r from-orange-500/15 via-amber-500/10 to-orange-500/15 shadow-xl shadow-orange-500/20 scale-[1.02]'
                      : 'border-orange-500/20 hover:border-orange-500/60 hover:bg-orange-500/5 hover:scale-[1.01] hover:shadow-lg hover:shadow-orange-500/10'
                    }
                    ${isPollEnded || userHasVoted || !user ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {/* Ripple effect on click */}
                  {isRippling && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-8 h-8 bg-orange-500/30 rounded-full animate-vote-ripple" />
                    </div>
                  )}
                  
                  {/* Hover glow */}
                  <div className="absolute inset-0 opacity-0 group-hover/option:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-orange-500/10 via-amber-500/5 to-orange-500/10 rounded-xl" />
                  
                  <div className="flex items-center gap-3 relative z-10">
                    <div className={`
                      h-7 w-7 rounded-full border-2 transition-all duration-300 flex items-center justify-center
                      ${isSelected
                        ? 'border-orange-500 bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/40'
                        : 'border-foreground/30 group-hover/option:border-orange-500/60'
                      }
                    `}>
                      {isSelected && (
                        <div className="h-3 w-3 rounded-full bg-white animate-scale-in-bounce" />
                      )}
                    </div>
                    <span className="text-left">{option.label}</span>
                  </div>

                  {/* Fire icon for selected */}
                  {isSelected && (
                    <Flame className="h-5 w-5 text-orange-500 animate-fire-dance" />
                  )}
                </button>
              )}
            </div>
          );
        })}

        {/* Multi-choice submit button */}
        {poll.pollType === 'multiplechoice' && !shouldShowResults && selectedOptions.length > 0 && !isPollEnded && (
          <Button
            onClick={handleVote}
            disabled={isVoting || !user}
            className="w-full h-14 text-lg font-black bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 border-0 text-white relative overflow-hidden group/submit"
            size="lg"
          >
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 opacity-0 group-hover/submit:opacity-100 transition-opacity duration-300" />
            
            <span className="relative z-10 flex items-center gap-2">
              {isVoting ? (
                <>
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5 animate-bounce-subtle" />
                  Submit {selectedOptions.length} {selectedOptions.length === 1 ? 'Vote' : 'Votes'}
                </>
              )}
            </span>
          </Button>
        )}
      </CardContent>

      <CardFooter className="flex items-center justify-between pt-4 border-t border-orange-500/10 gap-2 relative z-10">
        <div className="flex items-center gap-2 text-sm font-bold glass dark:glass-dark px-4 py-2.5 rounded-xl border border-orange-500/20">
          <Users className="h-4 w-4 text-orange-500" />
          <span className="text-foreground/70 tabular-nums">
            <AnimatedNumber value={totalVotes} /> {totalVotes === 1 ? 'vote' : 'votes'}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="glass dark:glass-dark hover:bg-orange-500/10 font-bold border border-orange-500/20 hover:border-orange-500/40 transition-all group/share"
          >
            <Share2 className="h-4 w-4 sm:mr-2 transition-transform group-hover/share:scale-110 group-hover/share:rotate-12" />
            <span className="hidden sm:inline">Share</span>
          </Button>
          {user && (
            <ShareToNostrDialog
              pollQuestion={poll.question}
              pollUrl={getPollUrl()}
              disabled={isVoting}
            />
          )}
        </div>
      </CardFooter>
      </Card>
    </>
  );
}
